import * as React from 'react';

import { Celula, type CampoDeCor } from '@/componentes/celula';
import { pontosHexagono, LARGURA_POR_RAIO } from '@/design/favo';
import { cn } from '@/lib/utils';

/* Regua — vinte microcélulas e um entalhe.
 *
 * O número diz QUANTO; a régua diz ONDE isso cai. Um "17,2%" sozinho não
 * responde se é pouco: ao lado de um entalhe na meta de 37,6%, responde em
 * meio segundo. É a mesma ideia do favo de cem células, em escala de linha.
 *
 * `fracao` e `meta` são posições na régua, de 0 a 1, não percentuais do
 * negócio: quem decide a escala (0 a 40% de margem, 0 a 5 projetos/mês) é a
 * tela, porque é ela que sabe o que é "muito" naquele indicador. */

export type TomRegua = 'acao' | 'positivo' | 'atencao' | 'negativo' | 'info' | 'neutro';

const TINTA_REGUA: Record<TomRegua, string> = {
  acao: 'var(--acao)',
  positivo: 'var(--positivo)',
  atencao: 'var(--atencao)',
  negativo: 'var(--negativo)',
  info: 'var(--info)',
  neutro: 'var(--tinta-3)',
};

export type ReguaProps = {
  /** Posição do valor na régua, de 0 a 1. Valores fora da faixa são grampeados. */
  fracao: number;
  /** Posição da meta, de 0 a 1. Sem meta, a régua não desenha entalhe. */
  meta?: number | undefined;
  tom?: TomRegua | undefined;
  celulas?: number | undefined;
  className?: string | undefined;
};

export function Regua({ fracao, meta, tom = 'acao', celulas = 20, className }: ReguaProps) {
  const grampeada = Math.min(1, Math.max(0, Number.isFinite(fracao) ? fracao : 0));
  const r = 6;
  const passo = LARGURA_POR_RAIO * r;
  const largura = passo * celulas;
  const altura = 2 * r + 2;
  // Ao menos uma célula acesa: régua totalmente apagada parece defeito, não zero.
  const acesas = Math.max(1, Math.round(grampeada * celulas));
  const cor = TINTA_REGUA[tom];

  return (
    <svg
      viewBox={`0 0 ${largura} ${altura}`}
      className={cn('block h-auto w-full', className)}
      aria-hidden="true"
      focusable="false"
    >
      {Array.from({ length: celulas }, (_, i) => (
        <polygon
          key={i}
          points={pontosHexagono(passo / 2 + i * passo, r + 1, r - 0.9)}
          fill={i < acesas ? cor : 'var(--tinta-4)'}
          opacity={i < acesas ? 1 : 0.45}
        />
      ))}
      {meta === undefined ? null : (
        <line
          x1={Math.min(1, Math.max(0, meta)) * largura}
          y1="0"
          x2={Math.min(1, Math.max(0, meta)) * largura}
          y2={altura}
          stroke="var(--tinta-2)"
          strokeWidth="1.5"
        />
      )}
    </svg>
  );
}

/* Indicador — a célula de KPI.
 *
 * Rótulo em cima, cifra colada no rodapé do bloco de texto, régua embaixo. A
 * cifra encosta na base por `margin-top:auto` e não por altura fixa: quatro
 * indicadores lado a lado com textos de apoio de tamanhos diferentes
 * continuam com as quatro cifras alinhadas, que é a única razão de existir
 * uma fileira de KPIs.
 *
 * O campo é escolha de conteúdo, igual ao da célula: mint informa, telha
 * perde, areia constata, mel custa. */

export type IndicadorProps = {
  rotulo: string;
  /** A cifra. Normalmente um `<Valor>` com tamanho "titulo". */
  children: React.ReactNode;
  campo?: CampoDeCor | undefined;
  /** Uma linha explicando de onde o número veio. */
  apoio?: string | undefined;
  /** Texto curto à direita, sob a régua: "meta 37,6%", "escala 0 a 5". */
  referencia?: string | undefined;
  regua?: ReguaProps | undefined;
  /** Canto superior direito: um Hexinho, um SeloEstado, um botão de ajuda. */
  marcador?: React.ReactNode;
  assenta?: boolean | undefined;
  atraso?: number | undefined;
  className?: string | undefined;
};

export function Indicador({
  rotulo,
  children,
  campo = 'nata',
  apoio,
  referencia,
  regua,
  marcador,
  assenta,
  atraso,
  className,
}: IndicadorProps) {
  return (
    <Celula
      campo={campo}
      {...(assenta === undefined ? {} : { assenta })}
      {...(atraso === undefined ? {} : { atraso })}
      className={cn('flex min-h-indicador flex-col gap-3', className)}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="rotulo">{rotulo}</span>
        {marcador}
      </div>

      <div className="mt-auto">
        {children}
        {apoio ? <p className="apoio-nota mt-1">{apoio}</p> : null}
      </div>

      {regua ? (
        <div>
          <Regua {...regua} />
          {/* A régua não leva rótulo: ela está encostada no número que explica,
              e nomeá-la só acrescentava uma linha de texto que ninguém lê. */}
          {referencia ? (
            <p className="numero text-tinta-2 mt-1 text-right text-nota whitespace-nowrap">
              {referencia}
            </p>
          ) : null}
        </div>
      ) : referencia ? (
        <span className="numero text-tinta-2 text-nota">{referencia}</span>
      ) : null}
    </Celula>
  );
}
